import { assigneesOf, can, canAssignItem, flowLane, fmtDate, nameList, kindFlow, laneOf, REACTIONS, reviewWaitingFor, tagShort, type BoardItem, type BoardMember, type Lane, type StageStamp, type Viewer } from "@boem/core/shared";
import { useContext, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type DragEvent } from "react";
import { cardModel, type CardAction, type MenuGroupModel } from "../cardActions.ts";
import type { NotesFilter } from "../filters.ts";
import { Linkify } from "../util.tsx";
import { AssigneeButton, assigneesTip, AvatarStack, PersonAvatar } from "./Assign.tsx";
import { useBoards } from "../boards.ts";
import { OnOemuap } from "./Bridges.tsx";
import { CopiedFrom, movedLabel } from "./Lifecycle.tsx";
import { DueBadge, DueField } from "./Due.tsx";
import { GitHubBlock, PrChips } from "./GitHub.tsx";
import { Icon } from "./Icons.tsx";
import { blockedTitle, Links } from "./Links.tsx";
import { ShotStrip } from "./Media.tsx";
import { Menu, type MenuGroup } from "./Menu.tsx";
import { NotesBlock, NotesContext, notesDefault, Origin, threadOf, threadOpen, type OwnFlip } from "./Notes.tsx";
import { DraftBadge, itemNo, KindBadge, NumCol, PlaceLine, ProjectChip, Progress, SubItems } from "./Structure.tsx";
import { Working } from "./Working.tsx";

export type { CardAction } from "../cardActions.ts";

interface Props {
  item: BoardItem;
  viewer: Viewer;
  flash: boolean;
  /** opened from a link (/#item-N, e.g. OEM-185 on GitHub): a glow that stays until you click elsewhere or press Esc */
  selected?: boolean;
  /** run a card action (LogView: the api call or the dialog); may return a promise the picker waits for */
  onAction: (item: BoardItem, action: CardAction) => unknown;
  /** which notes to show: everyone's, only people's own, or only their AI apps' */
  notes?: NotesFilter;
  /** what's under an item, among the items this board has */
  kidsOf?: (num: number) => BoardItem[];
  /** the board's copy of an item (a linked one, to show its card in place) */
  itemOf?: (num: number) => BoardItem | undefined;
  /** the team, for the assignee picker */
  members?: BoardMember[];
  /** this card is the project whose board is open */
  here?: boolean;
  /** the project whose board or panel this is: its items show their number there (07) */
  scope?: number | null;
  /** false for a second copy (in a panel, or opened in place): no anchor to jump to */
  anchor?: boolean;
  /** shown in place under its parent's row (▸): its own rows don't open further in place */
  nested?: boolean;
  /** the card a panel is showing: no "Open in panel" on it */
  inPanel?: boolean;
  /** where the board says which project the item is in, instead of the place line: a chip in the badge
   *  row (the main board, the archive), or the heading it sits under (Group by project: no chip) */
  projectMark?: "chip" | "heading";
  /** the board shows only this item's project (its chip was clicked): the chip is pressed */
  chipOn?: boolean;
}

const NO_KIDS: BoardItem[] = [];
const noKids = () => NO_KIDS;
const noItem = () => undefined;
const NO_MEMBERS: BoardMember[] = [];

/** Collapse entries taller than the fold, the way the original board did. */
function ClippedText({ text }: { text: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [tall, setTall] = useState(false);
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const wasClipped = el.classList.contains("clip");
      el.classList.remove("clip");
      setTall(el.scrollHeight > 240);
      if (wasClipped) el.classList.add("clip");
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el.parentElement ?? el);
    return () => ro.disconnect();
  }, [text]);
  return (
    <>
      <div ref={ref} className={`txt${tall && !open ? " clip" : ""}`}>
        <Linkify text={text} />
      </div>
      {tall && (
        <button className="more" type="button" onClick={() => setOpen(!open)}>
          {open ? "Collapse" : "Read the full entry"}
        </button>
      )}
    </>
  );
}

/** Who has it, since when, who passed it over, and the next step - plus the trail. */
function Handoffs({ it, members }: { it: BoardItem; members?: { id: string }[] }) {
  const [open, setOpen] = useState(false);
  // on a personal board, someone who left it keeps what was theirs until it's reassigned (orgs.md 5)
  const personal = useBoards()?.org.kind === "personal";
  const people = assigneesOf(it);
  if (!people.length) return null;
  const trail = it.handoffs;
  const now = nameList(people.map((p) => p.name));
  const from = trail[trail.length - 1]?.from ?? null;
  const left = (id: string) => personal && !!members && !members.some((m) => m.id === id);
  // several people (OEM-285): each next step under whose it is; one person reads as before
  const notes = people.filter((p) => p.note);
  return (
    <div className="handoff">
      <div className="ho-line">
        With{" "}
        {people.map((p, i) => (
          <span key={p.id}>
            {i ? (i === people.length - 1 ? " and " : ", ") : ""}
            {p.name}
            {left(p.id) ? " (left the board)" : ""}
          </span>
        ))}
        {people.length === 1 && people[0]!.at ? ` since ${fmtDate(people[0]!.at)}` : ""}
        {from && from !== now ? ` (was ${from})` : ""}
      </div>
      {notes.map((p) => (
        <div className="ho-next" key={p.id}>
          <span className="ho-lbl">{people.length > 1 ? `Next step · ${p.name}` : "Next step"}</span>
          <span>
            <Linkify text={p.note!} />
          </span>
        </div>
      ))}
      {trail.length > 1 && (
        <>
          <button type="button" className="ho-toggle" onClick={() => setOpen(!open)}>
            Handoffs &middot; {trail.length}
          </button>
          {open && (
            <ul className="ho-list">
              {trail
                .slice()
                .reverse()
                .map((e, i) => (
                  <li key={i}>
                    <span className="ho-when">{fmtDate(e.at)}</span>
                    {`${e.from ? `${e.from} → ` : "→ "}${e.to ?? "unassigned"}${e.note ? " — " : ""}`}
                    {e.note && (
                      <span className="ho-note">
                        <Linkify text={e.note} />
                      </span>
                    )}
                  </li>
                ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

/** The fix flow so far: who developed, deployed and verified it, when, and what they said. */
function FlowTrail({ it }: { it: BoardItem }) {
  const steps = (
    [
      ["Developed", it.developed],
      ["Deployed", it.deployed],
      ["Verified", it.verified],
    ] as [string, StageStamp | null][]
  ).filter((s): s is [string, StageStamp] => !!s[1]);
  if (!steps.length && !it.archived) return null;
  return (
    <div className="flow-trail" data-lane={laneOf(it)}>
      {steps.map(([label, s]) => (
        <div className="ft-step" key={label}>
          <span className="ft-lbl">{label}</span>
          <span className="ft-who">
            {s.by ?? "someone"} &middot; {fmtDate(s.at)}
            {s.recordedBy && <span className="ft-rec"> &middot; recorded by {s.recordedBy}</span>}
          </span>
          {s.note && (
            <span className="ft-note">
              <Linkify text={s.note} />
            </span>
          )}
        </div>
      ))}
      {it.archived && (
        <div className="ft-step">
          <span className="ft-lbl">Archived</span>
          <span className="ft-who">
            {fmtDate(it.archived.at)}
            {it.archived.by ? ` · by ${it.archived.by}` : ""}
          </span>
        </div>
      )}
    </div>
  );
}

const PILL: Record<Lane, string> = { todo: "To do", review: "PR review", deploy: "Developed", verify: "Deployed", closed: "Closed" };

/**
 * An item's card (docs/design/actions.md, variant A as refined): content first. Quiet icons at the
 * head's right end (who has it, ⧉ ↗ + link …), one primary button for the next step of its flow (a
 * split button in the deploy and verify queues), Agree / Question / Claim chips, the note composer at
 * the end of an open thread (else a Note chip), everything else in the "…" menu. What a card offers
 * is cardModel's (kindActions + can()); this draws it.
 */
export function Card({
  item: it,
  viewer,
  flash,
  selected = false,
  onAction,
  notes = "all",
  kidsOf = noKids,
  itemOf = noItem,
  members = NO_MEMBERS,
  here = false,
  scope = null,
  anchor = true,
  nested = false,
  inPanel = false,
  projectMark,
  chipOn = false,
}: Props) {
  const kids = kidsOf(it.num);
  const [dropOver, setDropOver] = useState(false);
  // a project's description folds behind "About"
  const [about, setAbout] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const moreBtn = useRef<HTMLButtonElement>(null);
  const caretBtn = useRef<HTMLButtonElement>(null);
  const act = (a: CardAction) => onAction(it, a);

  // the notes: the toolbar's state (notes on every card), or this card's own flip since it was last pressed
  const view = useContext(NotesContext);
  const id = useId();
  const [own, setOwn] = useState<OwnFlip | null>(null);
  const notesOpen = threadOpen(view, own, nested);
  const flipRef = useRef(view.flip);
  flipRef.current = view.flip;
  useEffect(() => () => flipRef.current(id, false), [id]);
  const toggleNotes = () => {
    const next = !notesOpen;
    setOwn({ gen: view.gen, open: next });
    view.flip(id, next !== notesDefault(view, nested));
  };

  // drop screenshots straight onto a card to add them
  const canDrop = can(viewer, "attach");
  const dragFiles = (e: DragEvent) => canDrop && Array.from(e.dataTransfer?.types ?? []).includes("Files");
  const mineRx = it.reactions.find((r) => r.name === viewer.name)?.kind ?? null;
  // a verified item's close is the verification itself - the trail shows it
  const closedFromBoard = it.status === "closed" && it.closedBy && !it.verified;
  const live = !it.archived;
  const closed = it.status === "closed";
  const project = it.kind === "project";
  // how it moves: the fix flow, To do -> Done (a task), or none (a project)
  const flow = kindFlow(it.kind);
  const ref = itemNo(it, scope);
  // on the board it lives on: its project's board, or the main board for an item in none (the board column only)
  const onItsBoard = anchor && !nested && !inPanel && (scope ?? null) === (it.project?.num ?? null);
  // Copy to my board: OEMUAP or someone else's personal board, and I have a board of my own (orgs.md 1.5, 6.2)
  const boards = useBoards();
  const myBoard = boards?.list?.boards.find((b) => b.mine && b.exists && b.kind === "personal");
  const personal = boards?.org.kind === "personal";
  const copyOut = !!myBoard && (!personal || myBoard.slug !== boards?.org.slug);
  // GitHub is OEMUAP's (docs/github.md): the GitHub menu group, the PR chips, the panel's GitHub block
  const github = boards?.org.kind === "company";
  const model = cardModel(it, viewer, { here, inPanel, nested, onItsBoard, copyOut, personal, github }, ref);
  // its queue; a fix still To do with an open, non-draft PR from its project's repos is in PR review
  // (OEMUAP's: a personal board has no GitHub)
  const lane = flowLane(github ? it : { ...it, prs: undefined });
  const review = lane === "review";
  const waiting = review ? reviewWaitingFor(it).map((w) => w.name) : [];
  const mayLinkRemove = live;
  const thread = threadOf(it, notes);

  // who has it: a button that opens the picker for those who can assign or take it; a plain mark for the rest
  const people = assigneesOf(it);
  const picks = it.kind !== "project" && (canAssignItem(viewer, people.map((p) => p.id)) || can(viewer, "claim"));
  const avatar = picks ? (
    <AssigneeButton
      item={it}
      viewer={viewer}
      members={members}
      label={ref}
      title={it.title}
      onAct={(a) => onAction(it, a)}
      open={pickOpen}
      setOpen={setPickOpen}
    />
  ) : people.length ? (
    <span className={`hi hav${people.length > 1 ? " many" : ""}`} role="img" data-tip={assigneesTip(people)} aria-label={assigneesTip(people)}>
      {people.length > 1 ? <AvatarStack people={people} viewerId={viewer.id} /> : <PersonAvatar name={people[0]!.name} me={people[0]!.id === viewer.id} />}
    </span>
  ) : null;

  const groups = (gs: MenuGroupModel[]): MenuGroup[] =>
    gs.map((g) => ({
      title: g.title,
      entries: g.entries.map((x) => ({
        key: x.key,
        label: x.label,
        hint: x.hint,
        off: x.off,
        onSelect: () => (x.action === "assign" ? setPickOpen(true) : act(x.action)),
      })),
    }));
  const sheetHead = (
    <>
      <b>{ref}</b>
      {it.title}
    </>
  );
  // several people (OEM-285): their stack takes about two icons' room
  const iconCount = model.icons.length + 1 + (avatar ? (people.length > 1 && it.kind !== "project" ? 2 : 1) : 0);

  const body = <ClippedText text={it.body} />;
  const shots = project ? null : (
    <ShotStrip
      item={it}
      viewer={viewer}
      onOpen={(index) => act({ kind: "shot", index })}
      onRemove={(id) => act({ kind: "removeShot", id })}
      onRelease={(id) => act({ kind: "releaseShot", id })}
    />
  );
  // the screenshots added with a note, under it (on a project's card too)
  const noteShots = (noteId: string) => (
    <ShotStrip
      item={it}
      viewer={viewer}
      noteId={noteId}
      onOpen={(index) => act({ kind: "shot", index })}
      onRemove={(id) => act({ kind: "removeShot", id })}
      onRelease={(id) => act({ kind: "releaseShot", id })}
    />
  );
  // what's under it: one row each; ▸ opens one in place, one level deep (a card shown in place opens none)
  const subs = (
    <SubItems
      it={it}
      kids={kids}
      scope={scope}
      viewer={viewer}

      onPanel={(num) => act({ kind: "panel", num })}
      onGoto={(num) => act({ kind: "goto", num })}
      renderInline={
        nested
          ? undefined
          : (kid) => <Card item={kid} viewer={viewer} flash={false} onAction={onAction} notes={notes} kidsOf={kidsOf} itemOf={itemOf} members={members} scope={scope} anchor={false} nested />
      }
    />
  );
  // what it blocks, is blocked by, relates to or duplicates: the slot under the sub-items, same row grammar
  const links = (
    <Links
      it={it}
      viewer={viewer}
      scope={scope}
      itemOf={itemOf}
      onRemove={mayLinkRemove ? (link) => act({ kind: "unlink", link }) : undefined}
      onPanel={(num) => act({ kind: "panel", num })}
      onGoto={(num) => act({ kind: "goto", num })}
      renderInline={
        nested
          ? undefined
          : (other) => <Card item={other} viewer={viewer} flash={false} onAction={onAction} notes={notes} kidsOf={kidsOf} itemOf={itemOf} members={members} scope={scope} anchor={false} nested />
      }
    />
  );
  const closedBy = closedFromBoard && (
    <div className="closed-by">
      <span className="who">
        {flow === "done" ? "Done by" : "Closed by"} {it.closedBy}
        {it.closedAt ? ` · ${fmtDate(it.closedAt)}` : ""}
      </span>
      {it.closeNote && (
        <span className="why">
          <Linkify text={it.closeNote} />
        </span>
      )}
    </div>
  );

  // the bottom row: the note control, the chips, the primary on the right
  const p = model.primary;
  const mainBtn = p && (
    <button type="button" className="prim" data-to={p.to} title={p.title} disabled={p.disabled} onClick={() => act(p.action)}>
      {p.label}
    </button>
  );
  const noteCtl =
    model.note &&
    (notesOpen && thread.notes > 0 ? (
      <button type="button" className="compose" aria-label={`Add a note to ${ref}`} onClick={() => act({ kind: "note" })}>
        <Icon name="note" />
        <span className="lg">Add a note&hellip;</span>
      </button>
    ) : (
      <button type="button" className="rx note-chip" aria-label={`Add a note to ${ref}`} title="Add a note" onClick={() => act({ kind: "note" })}>
        <Icon name="note" />
        <span className="l">Note</span>
      </button>
    ));
  const bar = model.bar && (
    <div className="cbar" role="group" aria-label={`${ref}: next step, reactions, note`}>
      {noteCtl}
      {model.react &&
        REACTIONS.filter(([k]) => k !== "claim").map(([k, label]) => (
          <button
            key={k}
            type="button"
            className="rx"
            data-r={k}
            aria-pressed={mineRx === k}
            aria-label={label}
            title={k === "agree" ? "Agree: it happens to you too" : "Question: something about it is unclear"}
            disabled={model.reactOff}
            onClick={() => act({ kind: "react", value: mineRx === k ? null : (k as "agree" | "question") })}
          >
            <Icon name={k === "agree" ? "agree" : "question"} />
            <span className="l">{label}</span>
          </button>
        ))}
      {model.claim && (
        <button type="button" className="rx claim" data-r="claim" title="Take this item yourself" onClick={() => act({ kind: "claim" })}>
          Claim
        </button>
      )}
      {p &&
        (model.split ? (
          <span className="split" data-to={p.to}>
            {mainBtn}
            <button
              ref={caretBtn}
              type="button"
              className="prim prim-caret"
              data-to={p.to}
              aria-haspopup="menu"
              aria-expanded={splitOpen}
              aria-label={`Other next steps for ${ref}`}
              title={model.split.flatMap((g) => g.entries).length > 1 ? "More next steps…" : "Send back…"}
              onClick={() => setSplitOpen(!splitOpen)}
            >
              <Icon name="caret" />
            </button>
          </span>
        ) : (
          mainBtn
        ))}
    </div>
  );

  return (
    <article
      className={`card${nested ? " nested" : ""}${flash ? " flash" : ""}${selected ? " selected" : ""}${dropOver ? " drop-over" : ""}`}
      id={anchor ? `item-${it.num}` : undefined}
      data-status={it.status}
      data-lane={lane}
      data-prio={it.priority}
      data-kind={it.kind ?? undefined}
      data-num={it.num}
      data-open={menuOpen || splitOpen || pickOpen ? "1" : undefined}
      style={{ "--hn": iconCount } as CSSProperties}
      onDragOver={(e) => {
        if (!dragFiles(e)) return;
        e.preventDefault();
        setDropOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropOver(false);
      }}
      onDrop={(e) => {
        if (!dragFiles(e)) return;
        e.preventDefault();
        setDropOver(false);
        act({ kind: "attach", files: Array.from(e.dataTransfer.files) });
      }}
    >
      <div className="card-body">
        <NumCol it={it} scope={scope} />
        <div className="card-main">
          <div className="head">
            <KindBadge kind={it.kind} />
            {it.draft && <DraftBadge it={it} />}
            {flow === "none" ? (
              closed && <span className="pill s-closed l-closed">Closed</span>
            ) : flow === "done" ? (
              <span className={`pill s-${it.status} l-${closed ? "closed" : "todo"}`}>{closed ? "Done" : "To do"}</span>
            ) : (
              <span
                className={`pill s-${it.status} l-${lane}`}
                title={
                  review
                    ? `A pull request for it is open and ready for review${waiting.length ? `: waiting for ${waiting.join(", ")}` : ""}. It moves to Developed (the deploy queue) when it merges into the default branch`
                    : undefined
                }
              >
                {lane === "closed" && it.verified ? "Verified" : PILL[lane]}
              </span>
            )}
            {github && <PrChips prs={it.prs} />}
            {projectMark === "chip" && it.project && (
              <ProjectChip project={it.project} on={chipOn} onClick={() => act({ kind: "filterProject", num: it.project!.num })} />
            )}
            <DueBadge item={it} />
            {it.blocked && (
              <span className="role blocked-badge" title={blockedTitle(it, scope)}>
                blocked
              </span>
            )}
            {project && it.ownBoard && (
              <span className="role own-board-badge" title="Its items stay off the main board">
                own board
              </span>
            )}
            {it.locked && (
              <span className="role lock-badge" title="An admin locked it to its project: it can't be moved to another one (Organize says who)">
                locked
              </span>
            )}
            {!it.key && it.formerKeys.length > 0 && (
              <span className="role was-badge" title={`It left its project and goes by #${it.num} now. Reserved for it: ${it.formerKeys.join(", ")}. Searching either still finds it.`}>
                #{it.num} &middot; was {it.formerKeys[it.formerKeys.length - 1]}
              </span>
            )}
            {it.key && it.formerKeys.length > 0 && (
              <span className="role was-badge" title={`Its numbers in projects it left: ${it.formerKeys.join(", ")} (reserved for it)`}>
                was {it.formerKeys[it.formerKeys.length - 1]}
              </span>
            )}
            {(it.movedFrom ?? []).map((m) => (
              <span key={`${m.board}#${m.num}`} className="role was-badge" title={`A platform admin moved it here from ${m.board} on ${fmtDate(m.at)}, where it was #${m.num}${m.key ? ` (${m.key})` : ""}. Searching that still finds it.`}>
                {movedLabel(m)}
              </span>
            ))}
            {it.archived && <span className="role archived-badge">in the archive</span>}
            {it.nondev && (
              <span className="role nondev-badge" title="Kept on the board, not for the developers">
                Non-dev
              </span>
            )}
            {it.teamsResolved && (
              <span className="role teams-badge" title="The fix was agreed or confirmed in a Teams thread or call">
                resolved via Teams
              </span>
            )}
            {it.role && <span className="role">{it.role}</span>}
            {!project && <span className={`prio${it.priority === "high" ? " high" : ""}`}>{it.priority} priority</span>}
            {it.tags.map((t) => (
              <span key={t} className="tag">
                {tagShort(t)}
              </span>
            ))}
            {it.flag && <span className="flag">{it.flag}</span>}
            <span className="date">
              logged {fmtDate(it.addedAt)}
              {/* one name: who it came from (raised it), else who logged it; the other is in the tooltip */}
              {(it.openedBy ?? it.loggedBy) && (
                <>
                  {" by "}
                  <b
                    className="by"
                    title={it.openedBy && it.loggedBy && it.openedBy !== it.loggedBy ? `Raised by ${it.openedBy}, logged by ${it.loggedBy}` : undefined}
                  >
                    {it.openedBy ?? it.loggedBy}
                  </b>
                </>
              )}
              {it.closedAt ? ` · ${flow === "done" ? "done" : "closed"} ${fmtDate(it.closedAt)}` : ""}
            </span>
          </div>
          {/* the head icons: right after the head in reading and Tab order, drawn at its right end */}
          <div className="hicons" role="group" aria-label={`${ref}: quick actions`}>
            {avatar}
            {model.icons.map((x) => (
              <button key={x.key} type="button" className="hi" data-key={x.key} data-tip={x.tip} aria-label={x.label} onClick={() => act(x.action)}>
                <Icon name={x.icon} />
              </button>
            ))}
            <button
              ref={moreBtn}
              type="button"
              className="hi"
              data-key="more"
              data-tip="More"
              aria-label={`More actions for ${ref}`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <Icon name="more" />
            </button>
          </div>
          {project ? (
            <>
              {/* a project: its progress first, the description folded behind About, then its items */}
              <div className="proj-head">
                <h2 className="t">{it.title}</h2>
                <Progress it={it} />
              </div>
              {it.origin && <Origin o={it.origin} />}
              {closedBy}
              <FlowTrail it={it} />
              <Handoffs it={it} members={members} />
              <Working it={it} />
              {inPanel && <DueField item={it} viewer={viewer} onSet={(due) => act({ kind: "setDue", due })} />}
              {it.body.trim() && (
                <button type="button" className="about-btn" aria-expanded={about} onClick={() => setAbout(!about)}>
                  About <Icon name="chevron" />
                </button>
              )}
              {about && body}
              {subs}
              {links}
            </>
          ) : (
            <>
              <h2 className="t">{it.title}</h2>
              <PlaceLine it={it} withProject={!projectMark} />
              {it.copiedFrom && !it.bridge && <CopiedFrom c={it.copiedFrom} />}
              {it.origin && <Origin o={it.origin} />}
              {closedBy}
              <FlowTrail it={it} />
              <Handoffs it={it} members={members} />
              <Working it={it} />
              {inPanel && <DueField item={it} viewer={viewer} onSet={(due) => act({ kind: "setDue", due })} />}
              {github && inPanel && (
                <GitHubBlock
                  it={it}
                  viewer={viewer}
                  onCopy={(text, what) => act({ kind: "copyText", text, what })}
                  onLink={() => act({ kind: "linkPr" })}
                  onUnlink={(pr) => act({ kind: "unlinkPr", pr })}
                />
              )}
              {/* a bug leads with what it looks like (sub-items below); a feature with what's under it;
                  everything else with what it says (design 4.11) */}
              {it.kind === "bug" ? (
                <>
                  {shots}
                  {body}
                  {subs}
                  {links}
                </>
              ) : it.kind === "feature" ? (
                <>
                  {subs}
                  {links}
                  {body}
                  {shots}
                </>
              ) : (
                <>
                  {body}
                  {shots}
                  {subs}
                  {links}
                </>
              )}
            </>
          )}
        </div>

        {it.bridge && <OnOemuap b={it.bridge} onSend={personal && live && can(viewer, "bridge") ? () => act({ kind: "bridgeSend" }) : undefined} />}
        <NotesBlock
          item={it}
          filter={notes}
          open={notesOpen}
          onToggle={toggleNotes}
          onPanel={nested ? undefined : () => act({ kind: "notesPanel" })}
          onEdit={can(viewer, "note") ? (noteId) => act({ kind: "editNote", noteId }) : undefined}
          label={ref}
          shots={noteShots}
        />
        {bar}
      </div>
      {menuOpen && <Menu anchor={moreBtn} groups={groups(model.menu)} label={`${ref}: more actions`} sheetHead={sheetHead} onClose={() => setMenuOpen(false)} />}
      {splitOpen && model.split && <Menu anchor={caretBtn} groups={groups(model.split)} label={`${ref}: next step`} sheetHead={sheetHead} onClose={() => setSplitOpen(false)} />}
    </article>
  );
}
